import {
  CONTENT_VERSION,
  GameRoom,
  MAX_PLAYERS,
  PROTOCOL_VERSION,
  type ClientMessage,
  type GameSocket,
  type HandshakeMessage,
  type PlayerHostPlaytestCredential,
  type ServerMessage,
} from "../../../../packages/game-core/src";

interface PeerContext {
  playerId?: string;
  token?: string;
  socket: BridgeSocket;
}

export interface PlayerHostedPlaytestPublication {
  state: string;
  credentials: PlayerHostPlaytestCredential[];
}

export interface PlayerHostedRoomOptions {
  roomCode: string;
  hostName: string;
  seed?: string;
  onLocalMessage(message: ServerMessage): void;
  onPeerMessage(peerId: string, payload: string): void;
}

export class PlayerHostedRoom {
  readonly room: GameRoom;

  private readonly localSocket: BridgeSocket;
  private readonly localPlayerId: string;
  private readonly localToken: string;
  private readonly peers = new Map<string, PeerContext>();
  private readonly onPeerMessage: PlayerHostedRoomOptions["onPeerMessage"];
  private readonly tickTimer: number;
  private previousTickAt = performance.now();

  constructor(options: PlayerHostedRoomOptions) {
    this.onPeerMessage = options.onPeerMessage;
    this.room = new GameRoom({
      code: options.roomCode,
      seed: options.seed?.trim() || undefined,
    });
    this.localSocket = new BridgeSocket((payload) => {
      const message = parseServerMessage(payload);
      if (message) {
        options.onLocalMessage(message);
      }
    });
    const joined = this.room.join(options.hostName, this.localSocket);
    this.localPlayerId = joined.playerId;
    this.localToken = joined.token;
    this.room.sendWelcome(joined.playerId, joined.token, false);
    this.tickTimer = window.setInterval(() => this.tick(), 50);
  }

  addPeer(peerId: string): void {
    if (this.peers.has(peerId) || this.peers.size >= MAX_PLAYERS - 1) {
      return;
    }
    const socket = new BridgeSocket((payload) =>
      this.onPeerMessage(peerId, payload),
    );
    this.peers.set(peerId, { socket });
  }

  removePeer(peerId: string): void {
    const context = this.peers.get(peerId);
    if (!context) {
      return;
    }
    if (context.playerId) {
      this.room.disconnect(context.playerId, context.socket);
    }
    context.socket.close(1000, "Peer left");
    this.peers.delete(peerId);
  }

  receivePeerMessage(peerId: string, payload: string): void {
    const context = this.peers.get(peerId);
    if (!context) {
      return;
    }
    const message = parseClientMessage(payload);
    if (!message) {
      context.socket.send(
        JSON.stringify({
          type: "error",
          code: "BAD_JSON",
          message: "玩家消息不是有效 JSON。",
          protocolVersion: PROTOCOL_VERSION,
        } satisfies ServerMessage & { protocolVersion: number }),
      );
      return;
    }

    if (context.playerId) {
      this.room.handleMessage(context.playerId, message);
      return;
    }

    if (!isGuestHandshake(message)) {
      this.sendHandshakeError(context.socket, "请先加入或恢复房间。");
      return;
    }

    if (
      message.protocolVersion !== PROTOCOL_VERSION ||
      message.contentVersion !== CONTENT_VERSION
    ) {
      this.sendHandshakeError(
        context.socket,
        `客户端版本不兼容：需要协议 ${PROTOCOL_VERSION} / 内容 ${CONTENT_VERSION}；P4.11 采矿客户端与快照已封存。`,
      );
      return;
    }

    try {
      if (message.type === "join") {
        const joined = this.room.join(message.name, context.socket);
        context.playerId = joined.playerId;
        context.token = joined.token;
        this.room.sendWelcome(joined.playerId, joined.token, false);
        return;
      }
      const resumed = this.room.reconnect(
        message.playerId,
        message.token,
        context.socket,
      );
      context.playerId = resumed.playerId;
      context.token = resumed.token;
      this.room.sendWelcome(resumed.playerId, resumed.token, true);
    } catch (error) {
      this.sendHandshakeError(
        context.socket,
        error instanceof Error ? error.message : "无法加入玩家主机房间。",
      );
    }
  }

  sendLocal(message: ClientMessage): void {
    this.room.handleMessage(this.localPlayerId, message);
  }

  getPlaytestPublication(): PlayerHostedPlaytestPublication | undefined {
    const state = this.room.getPlaytestState(
      this.localPlayerId,
      this.localToken,
    );
    if (
      state.phase !== "victory" &&
      state.phase !== "draw" &&
      state.phase !== "defeat"
    ) {
      return undefined;
    }
    const credentials: PlayerHostPlaytestCredential[] = [
      {
        playerId: this.localPlayerId,
        token: this.localToken,
      },
    ];
    for (const context of this.peers.values()) {
      if (context.playerId && context.token) {
        credentials.push({
          playerId: context.playerId,
          token: context.token,
        });
      }
    }
    if (credentials.length !== state.players.length) {
      return undefined;
    }
    return {
      state: JSON.stringify(state),
      credentials,
    };
  }

  close(): void {
    window.clearInterval(this.tickTimer);
    this.localSocket.close(1000, "Host stopped");
    for (const context of this.peers.values()) {
      context.socket.close(1000, "Host stopped");
    }
    this.peers.clear();
  }

  private tick(): void {
    const now = performance.now();
    const delta = Math.min(100, Math.max(0, now - this.previousTickAt));
    this.previousTickAt = now;
    this.room.tick(Date.now(), delta);
  }

  private sendHandshakeError(socket: BridgeSocket, message: string): void {
    socket.send(
      JSON.stringify({
        type: "error",
        code: "HANDSHAKE_REJECTED",
        message,
      } satisfies ServerMessage),
    );
  }
}

class BridgeSocket implements GameSocket {
  readyState = 1;

  constructor(private readonly deliver: (payload: string) => void) {}

  send(payload: string): void {
    if (this.readyState === 1) {
      this.deliver(payload);
    }
  }

  close(_code?: number, _reason?: string): void {
    this.readyState = 3;
  }
}

function parseClientMessage(payload: string): ClientMessage | undefined {
  try {
    return JSON.parse(payload) as ClientMessage;
  } catch {
    return undefined;
  }
}

function parseServerMessage(payload: string): ServerMessage | undefined {
  try {
    return JSON.parse(payload) as ServerMessage;
  } catch {
    return undefined;
  }
}

function isGuestHandshake(
  message: ClientMessage,
): message is Extract<HandshakeMessage, { type: "join" | "reconnect" }> {
  return message.type === "join" || message.type === "reconnect";
}
